import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';

import workflowTemplateService from '../../../services/workflowTemplateService';
import templateCategoryService from '../../../services/templateCategoryService';

const WorkflowCanvas = ({ nodes, connections, onNodeSelect, onNodeMove, onNodeUpdate, onNodeAdd, onConnectionAdd, onConnectionDelete, selectedNodeId, zoomLevel, onZoomChange, isSelectionMode, selectedNodes, setSelectedNodes }) => {
  const canvasRef = useRef(null);
  const { user } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [draggedNode, setDraggedNode] = useState(null);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [connectionStart, setConnectionStart] = useState(null);
  const [tempConnection, setTempConnection] = useState(null);
  const [collapsedContainers, setCollapsedContainers] = useState(new Set());
  const [selectionBox, setSelectionBox] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateCategory, setTemplateCategory] = useState('custom');
  const [templateCategories, setTemplateCategories] = useState([]);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [hoveredConnection, setHoveredConnection] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);

  // Add this helper function to calculate labor hours
  const calculateLaborHours = (taskNodeId) => {
    // Find all labor nodes connected to this task node
    const connectedLaborNodes = connections
      ?.filter(conn => conn?.target === taskNodeId)
      ?.map(conn => nodes?.find(n => n?.id === conn?.source))
      ?.filter(node => {
        return (
          node?.node_type === 'labor' ||
          node?.node_type === 'labour_' ||
          node?.type?.toLowerCase()?.includes('labor') ||
          node?.type?.toLowerCase()?.includes('labour') ||
          node?.name?.toLowerCase()?.includes('labor') ||
          node?.name?.toLowerCase()?.includes('labour') ||
          node?.category === 'labor'
        );
      });

    // Sum up labor hours from connected labor nodes
    const totalHours = connectedLaborNodes?.reduce((sum, laborNode) => {
      const hours = parseFloat(laborNode?.data?.laborHours) || 0;
      return sum + hours;
    }, 0);

    return totalHours;
  };

  // Expose nodes and connections globally for PropertiesPanel to access
  useEffect(() => {
    window.__workflowNodes = nodes;
    window.__workflowConnections = connections;
  }, [nodes, connections]);

  useEffect(() => {
    loadTemplateCategories();
  }, []);

  const loadTemplateCategories = async () => {
    try {
      const categoriesData = await templateCategoryService?.getAllCategories();
      setTemplateCategories(categoriesData);
    } catch (err) {
      console.error('Error loading template categories:', err);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    const rect = canvasRef?.current?.getBoundingClientRect();
    const x = (e?.clientX - rect?.left - panOffset?.x) / zoomLevel;
    const y = (e?.clientY - rect?.top - panOffset?.y) / zoomLevel;

    try {
      const jsonData = e?.dataTransfer?.getData('application/json');
      if (!jsonData || jsonData?.trim() === '') {
        console.warn('No valid data in drag operation');
        return;
      }
      const dragData = JSON.parse(jsonData);
      
      // Check if it's a template or a single node
      if (dragData?.type === 'template' && dragData?.template) {
        // Handle template drop - add all nodes and connections
        const template = dragData?.template;
        const nodeIdMap = {}; // Map old IDs to new IDs
        const newNodes = [];
        
        // Create new nodes with updated IDs and positions
        template?.nodes?.forEach((templateNode, index) => {
          const newNodeId = `node-${Date.now()}-${index}-${Math.random()?.toString(36)?.substr(2, 9)}`;
          nodeIdMap[templateNode?.id] = newNodeId;
          
          // Check if this is a material node
          const isMaterialNode = 
            templateNode?.type?.toLowerCase()?.includes('material') ||
            templateNode?.name?.toLowerCase()?.includes('material') ||
            templateNode?.category === 'resources';
          
          const newNode = {
            id: newNodeId,
            type: templateNode?.type,
            name: templateNode?.name,
            icon: templateNode?.icon,
            color: templateNode?.color,
            category: templateNode?.category,
            node_type: templateNode?.node_type, // CRITICAL: Preserve node_type for PropertiesPanel detection
            position: {
              x: x + (templateNode?.position?.x || 0),
              y: y + (templateNode?.position?.y || 0)
            },
            data: {
              ...templateNode?.data,
              isContainer: templateNode?.data?.isContainer || false,
              ...(isMaterialNode && {
                uid: newNodeId,
                createdBy: user?.email || 'Unknown User'
              })
            }
          };
          newNodes?.push(newNode);
        });
        
        // Create new connections with updated IDs
        const newConnections = [];
        if (template?.connections && Array.isArray(template?.connections)) {
          template?.connections?.forEach((conn, index) => {
            newConnections?.push({
              id: `conn-${Date.now()}-${index}-${Math.random()?.toString(36)?.substr(2, 9)}`,
              source: nodeIdMap?.[conn?.source] || conn?.source,
              target: nodeIdMap?.[conn?.target] || conn?.target,
              type: conn?.type || 'sequence'
            });
          });
        }
        
        // CRITICAL FIX: Call onNodeAdd with special flag to add all nodes at once
        // This prevents state update race conditions
        if (newNodes?.length > 0) {
          // Add first node to trigger the batch
          onNodeAdd({ type: 'template-batch', nodes: newNodes, connections: newConnections });
        }
        
      } else {
        // Handle single node drop (existing behavior)
        const nodeData = dragData;
        
        // Check if this is a material/resource node
        const isMaterialNode = 
          nodeData?.category === 'resources' || nodeData?.node_type?.toLowerCase()?.includes('material') ||
          nodeData?.id?.toLowerCase()?.includes('resource') ||
          nodeData?.id?.toLowerCase()?.includes('material') ||
          nodeData?.name?.toLowerCase()?.includes('material');
        
        const nodeId = `node-${Date.now()}`;
        
        const newNode = {
          id: nodeId,
          type: nodeData?.id,
          name: isMaterialNode ? '' : nodeData?.name, // Empty name for material nodes
          icon: nodeData?.icon,
          color: nodeData?.color,
          category: nodeData?.category,
          node_type: nodeData?.node_type, // CRITICAL: Preserve node_type for PropertiesPanel detection
          position: { x, y },
          data: {
            duration: 0,
            workers: 0,
            status: 'pending',
            ...(isMaterialNode && {
              uid: nodeId,
              createdBy: user?.email || 'Unknown User',
              quantity: 0,
              costPerUnit: 0,
              generalNode: ''
            })
          }
        };
        console.log('=== WorkflowCanvas: Creating Node ===');
        console.log('Node Data from Drag:', nodeData);
        console.log('New Node Created:', newNode);
        console.log('node_type preserved:', newNode?.node_type);
        console.log('====================================');
        onNodeAdd(newNode);
      }
    } catch (error) {
      console.error('Error adding node/template:', error);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleNodeMouseDown = (e, node) => {
    if (e?.button !== 0) return;
    e?.stopPropagation();
    
    if (isSelectionMode) {
      // Toggle node selection in selection mode
      setSelectedNodes(prev => {
        const newSet = new Set(prev);
        if (newSet?.has(node?.id)) {
          newSet?.delete(node?.id);
        } else {
          newSet?.add(node?.id);
        }
        return newSet;
      });
    } else {
      setIsDragging(true);
      setDraggedNode(node);
      onNodeSelect(node?.id);
    }
  };

  const handleNodeRightClick = (e, node) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    // Select the node when right-clicked
    onNodeSelect(node?.id);
    
    const rect = canvasRef?.current?.getBoundingClientRect();
    setContextMenu({
      nodeId: node?.id,
      x: e?.clientX - rect?.left,
      y: e?.clientY - rect?.top,
      node: node
    });
  };

  const handleStatusChange = (nodeId, newStatus) => {
    const node = nodes?.find(n => n?.id === nodeId);
    if (node && onNodeUpdate) {
      const updatedNode = {
        ...node,
        data: {
          ...node?.data,
          status: newStatus
        }
      };
      onNodeUpdate(nodeId, updatedNode);
    }
    setContextMenu(null);
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu) {
        closeContextMenu();
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu]);

  const handleMouseMove = (e) => {
    if (isDragging && draggedNode) {
      const rect = canvasRef?.current?.getBoundingClientRect();
      const x = (e?.clientX - rect?.left - panOffset?.x) / zoomLevel;
      const y = (e?.clientY - rect?.top - panOffset?.y) / zoomLevel;
      onNodeMove(draggedNode?.id, { x, y });
    } else if (isPanning) {
      const dx = e?.clientX - panStart?.x;
      const dy = e?.clientY - panStart?.y;
      setPanOffset({ x: panOffset?.x + dx, y: panOffset?.y + dy });
      setPanStart({ x: e?.clientX, y: e?.clientY });
    } else if (tempConnection) {
      const rect = canvasRef?.current?.getBoundingClientRect();
      setTempConnection({
        ...tempConnection,
        endX: (e?.clientX - rect?.left - panOffset?.x) / zoomLevel,
        endY: (e?.clientY - rect?.top - panOffset?.y) / zoomLevel
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDraggedNode(null);
    setIsPanning(false);
    if (tempConnection) {
      setTempConnection(null);
      setConnectionStart(null);
    }
  };

  const handleCanvasMouseDown = (e) => {
    if (e?.button === 0 && e?.target === canvasRef?.current) {
      if (isSelectionMode) {
        // Start selection box
        const rect = canvasRef?.current?.getBoundingClientRect();
        setSelectionBox({
          startX: e?.clientX - rect?.left,
          startY: e?.clientY - rect?.top,
          endX: e?.clientX - rect?.left,
          endY: e?.clientY - rect?.top
        });
      } else {
        setIsPanning(true);
        setPanStart({ x: e?.clientX, y: e?.clientY });
        onNodeSelect(null);
        setSelectedConnection(null); // Deselect connection when clicking canvas
      }
    }
  };

  const saveSelectedAsTemplate = () => {
    if (selectedNodes?.size === 0) {
      alert('Please select at least one node to save as template');
      return;
    }
    setShowSaveDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateName?.trim()) {
      alert('Please enter a template name');
      return;
    }

    try {
      setIsSavingTemplate(true);
      
      // Get selected nodes and their connections
      const selectedNodesList = nodes?.filter(n => selectedNodes?.has(n?.id));
      const selectedConnections = connections?.filter(c => 
        selectedNodes?.has(c?.source) && selectedNodes?.has(c?.target)
      );

      // Normalize positions (make relative to top-left of selection)
      const minX = Math.min(...selectedNodesList?.map(n => n?.position?.x));
      const minY = Math.min(...selectedNodesList?.map(n => n?.position?.y));
      
      const normalizedNodes = selectedNodesList?.map(node => ({
        ...node,
        position: {
          x: node?.position?.x - minX,
          y: node?.position?.y - minY
        }
      }));

      await workflowTemplateService?.createTemplate({
        name: templateName,
        description: templateDescription,
        nodes: normalizedNodes,
        connections: selectedConnections,
        category: templateCategory
      });

      // Reset dialog
      setShowSaveDialog(false);
      setTemplateName('');
      setTemplateDescription('');
      setTemplateCategory('custom');
      setSelectedNodes(new Set());
      
      alert('Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      alert(error?.message || 'Failed to save template');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleConnectionStart = (e, nodeId) => {
    e?.stopPropagation();
    const node = nodes?.find(n => n?.id === nodeId);
    if (node) {
      setConnectionStart(nodeId);
      setTempConnection({
        startX: node?.position?.x + 100,
        startY: node?.position?.y + 40,
        endX: node?.position?.x + 100,
        endY: node?.position?.y + 40
      });
    }
  };

  const handleConnectionEnd = (e, targetNodeId) => {
    e?.stopPropagation();
    if (connectionStart && connectionStart !== targetNodeId) {
      onConnectionAdd({
        id: `conn-${Date.now()}`,
        source: connectionStart,
        target: targetNodeId,
        type: 'sequence'
      });
    }
    setConnectionStart(null);
    setTempConnection(null);
  };

  const handleConnectionClick = (e, connectionId) => {
    e?.stopPropagation();
    setSelectedConnection(connectionId);
    onNodeSelect(null); // Deselect any selected node
  };

  const handleDeleteConnection = (connectionId) => {
    if (!connectionId) return;
    
    if (window.confirm('Delete this connection?')) {
      setSelectedConnection(null);
      // Use the prop callback instead of custom event
      if (onConnectionDelete) {
        onConnectionDelete(connectionId);
      }
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.key === 'Delete' || e?.key === 'Backspace') {
        if (selectedConnection) {
          e?.preventDefault();
          handleDeleteConnection(selectedConnection);
        }
      } else if (e?.key === 'Escape') {
        setSelectedConnection(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedConnection]);

  const toggleContainerCollapse = (e, nodeId) => {
    e?.stopPropagation();
    setCollapsedContainers(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(nodeId)) {
        newSet?.delete(nodeId);
      } else {
        newSet?.add(nodeId);
      }
      return newSet;
    });
  };

  const getConnectedNodeIds = (containerId) => {
    const connectedIds = new Set();
    const toProcess = [containerId];
    const processed = new Set();

    while (toProcess?.length > 0) {
      const currentId = toProcess?.shift();
      if (processed?.has(currentId)) continue;
      processed?.add(currentId);

      connections?.forEach(conn => {
        if (conn?.target === currentId && !connectedIds?.has(conn?.source)) {
          connectedIds?.add(conn?.source);
          toProcess?.push(conn?.source);
        }
      });
    }

    return connectedIds;
  };

  const isNodeVisible = (nodeId) => {
    for (const containerId of collapsedContainers) {
      const connectedIds = getConnectedNodeIds(containerId);
      if (connectedIds?.has(nodeId)) {
        return false;
      }
    }
    return true;
  };

  const isConnectionVisible = (connection) => {
    return isNodeVisible(connection?.source) && isNodeVisible(connection?.target);
  };

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, draggedNode, isPanning, panStart, panOffset, tempConnection]);

  const getConnectionPath = (sourceNode, targetNode) => {
    const startX = sourceNode?.position?.x + 100;
    const startY = sourceNode?.position?.y + 40;
    const endX = targetNode?.position?.x;
    const endY = targetNode?.position?.y + 40;

    const midX = (startX + endX) / 2;
    return `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
  };

  const getConnectionMidpoint = (sourceNode, targetNode) => {
    const startX = sourceNode?.position?.x + 100;
    const startY = sourceNode?.position?.y + 40;
    const endX = targetNode?.position?.x;
    const endY = targetNode?.position?.y + 40;
    
    return {
      x: (startX + endX) / 2,
      y: (startY + endY) / 2
    };
  };

  return (
    <div className="relative w-full h-full bg-background overflow-hidden">
      <div className="absolute top-4 left-4 z-10 flex items-center gap-2 bg-card rounded-xl shadow-lg border border-border px-4 py-2">
        <Button
          variant="ghost"
          size="sm"
          iconName="ZoomOut"
          onClick={() => onZoomChange(Math.max(0.25, zoomLevel - 0.25))}
          disabled={zoomLevel <= 0.25}
        />
        <span className="caption text-foreground min-w-[60px] text-center">
          {Math.round(zoomLevel * 100)}%
        </span>
        <Button
          variant="ghost"
          size="sm"
          iconName="ZoomIn"
          onClick={() => onZoomChange(Math.min(2, zoomLevel + 0.25))}
          disabled={zoomLevel >= 2}
        />
        <div className="w-px h-6 bg-border mx-2" />
        <Button
          variant="ghost"
          size="sm"
          iconName="Maximize2"
          onClick={() => {
            onZoomChange(1);
            setPanOffset({ x: 0, y: 0 });
          }}
        />
        {isSelectionMode && selectedNodes?.size > 0 && (
          <>
            <div className="w-px h-6 bg-border mx-2" />
            <Button
              variant="default"
              size="sm"
              iconName="Save"
              onClick={saveSelectedAsTemplate}
            >
              <span className="ml-2 text-sm">Save as Template ({selectedNodes?.size})</span>
            </Button>
          </>
        )}
      </div>
      {/* Save Template Dialog */}
      {showSaveDialog && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-xl shadow-2xl border border-border p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Save as Template</h3>
              <button
                onClick={() => setShowSaveDialog(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e?.target?.value)}
                  placeholder="e.g., Quality Check Flow"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description
                </label>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e?.target?.value)}
                  placeholder="Describe what this template does..."
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Category
                </label>
                <select
                  value={templateCategory}
                  onChange={(e) => setTemplateCategory(e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {templateCategories?.map((category) => (
                    <option key={category?.id} value={category?.categoryValue}>
                      {category?.categoryLabel}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSaveDialog(false)}
                  disabled={isSavingTemplate}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSaveTemplate}
                  disabled={isSavingTemplate || !templateName?.trim()}
                  className="flex-1"
                >
                  {isSavingTemplate ? 'Saving...' : 'Save Template'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div
        ref={canvasRef}
        className="w-full h-full cursor-grab active:cursor-grabbing"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onMouseDown={handleCanvasMouseDown}
        style={{
          backgroundImage: 'radial-gradient(circle, var(--color-muted) 1px, transparent 1px)',
          backgroundSize: `${20 * zoomLevel}px ${20 * zoomLevel}px`,
          backgroundPosition: `${panOffset?.x}px ${panOffset?.y}px`
        }}
      >
        <svg
          className="absolute inset-0"
          style={{
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
          }}
        >
          {connections?.map(conn => {
            const sourceNode = nodes?.find(n => n?.id === conn?.source);
            const targetNode = nodes?.find(n => n?.id === conn?.target);
            if (!sourceNode || !targetNode || !isConnectionVisible(conn)) return null;

            const isSelected = selectedConnection === conn?.id;
            const isHovered = hoveredConnection === conn?.id;
            const midpoint = getConnectionMidpoint(sourceNode, targetNode);

            return (
              <g key={conn?.id}>
                {/* Invisible thick path for easier clicking */}
                <path
                  d={getConnectionPath(sourceNode, targetNode)}
                  stroke="transparent"
                  strokeWidth="20"
                  fill="none"
                  transform={`translate(${panOffset?.x}, ${panOffset?.y}) scale(${zoomLevel})`}
                  style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                  onClick={(e) => handleConnectionClick(e, conn?.id)}
                  onMouseEnter={() => setHoveredConnection(conn?.id)}
                  onMouseLeave={() => setHoveredConnection(null)}
                />
                {/* Visible connection path */}
                <path
                  d={getConnectionPath(sourceNode, targetNode)}
                  stroke={isSelected ? 'var(--color-destructive)' : isHovered ? 'var(--color-primary)' : 'var(--color-primary)'}
                  strokeWidth={isSelected ? '3' : isHovered ? '3' : '2'}
                  fill="none"
                  transform={`translate(${panOffset?.x}, ${panOffset?.y}) scale(${zoomLevel})`}
                  style={{ pointerEvents: 'none' }}
                  strokeDasharray={isSelected ? '5,5' : 'none'}
                />
                <circle
                  cx={targetNode?.position?.x}
                  cy={targetNode?.position?.y + 40}
                  r={isSelected ? '5' : '4'}
                  fill={isSelected ? 'var(--color-destructive)' : 'var(--color-primary)'}
                  transform={`translate(${panOffset?.x}, ${panOffset?.y}) scale(${zoomLevel})`}
                  style={{ pointerEvents: 'none' }}
                />
                {/* Delete button on selected connection */}
                {isSelected && (
                  <g
                    transform={`translate(${panOffset?.x + midpoint?.x * zoomLevel}, ${panOffset?.y + midpoint?.y * zoomLevel})`}
                    style={{ pointerEvents: 'all', cursor: 'pointer' }}
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleDeleteConnection(conn?.id);
                    }}
                  >
                    <circle
                      cx="0"
                      cy="0"
                      r="12"
                      fill="var(--color-destructive)"
                      className="hover:opacity-80 transition-opacity"
                    />
                    <path
                      d="M -4 -4 L 4 4 M 4 -4 L -4 4"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </g>
                )}
              </g>
            );
          })}
          {tempConnection && (
            <path
              d={`M ${tempConnection?.startX} ${tempConnection?.startY} L ${tempConnection?.endX} ${tempConnection?.endY}`}
              stroke="var(--color-primary)"
              strokeWidth="2"
              strokeDasharray="5,5"
              fill="none"
              transform={`translate(${panOffset?.x}, ${panOffset?.y}) scale(${zoomLevel})`}
              style={{ pointerEvents: 'none' }}
            />
          )}
        </svg>

        <div
          className="absolute"
          style={{
            transform: `translate(${panOffset?.x}px, ${panOffset?.y}px) scale(${zoomLevel})`,
            transformOrigin: '0 0'
          }}
        >
          {nodes?.map(node => {
            if (!isNodeVisible(node?.id)) return null;
            
            const isContainer = node?.data?.isContainer || false;
            const isCollapsed = collapsedContainers?.has(node?.id);
            const isSelected = selectedNodes?.has(node?.id);
            
            // Check if this is a material node
            const isMaterialNode = 
              node?.category === 'resources' || node?.type?.toLowerCase()?.includes('resource') || 
              node?.type?.toLowerCase()?.includes('material') ||
              node?.name?.toLowerCase()?.includes('material');

            // Check if this is a labor node
            const isLaborNode = 
              node?.node_type === 'labor' ||
              node?.node_type === 'labour_' ||
              node?.type?.toLowerCase()?.includes('labor') ||
              node?.type?.toLowerCase()?.includes('labour') ||
              node?.name?.toLowerCase()?.includes('labor') ||
              node?.name?.toLowerCase()?.includes('labour') ||
              node?.category === 'labor';

            // Detect Base Task nodes
            const isBaseTaskNode =
              node?.node_type === 'task' ||
              node?.type === 'task' ||
              node?.category === 'tasks' ||
              (node?.type?.toLowerCase()?.includes('task') && !isLaborNode && !isMaterialNode);

            // Calculate collective labor hours for Base Task nodes
            const calculatedLaborHours = isBaseTaskNode ? calculateLaborHours(node?.id) : 0;

            return (
              <div
                key={node?.id}
                className={`absolute bg-card rounded-xl shadow-lg border-2 transition-all duration-250 ${isSelectionMode ? 'cursor-pointer' : 'cursor-move'} ${
                  isSelected ? 'border-green-500 shadow-green-500/50' : selectedNodeId === node?.id ? 'border-primary shadow-xl' : 'border-border'
                }`}
                style={{
                  left: `${node?.position?.x}px`,
                  top: `${node?.position?.y}px`,
                  width: '200px',
                  minHeight: '80px'
                }}
                onMouseDown={(e) => handleNodeMouseDown(e, node)}
                onContextMenu={(e) => handleNodeRightClick(e, node)}
              >
                {isSelected && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <Icon name="Check" size={14} color="white" />
                  </div>
                )}
                {/* Visual confirmation badge for saved properties */}
                {node?.data?.lastSaved && (
                  <div className="absolute -top-2 -left-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center animate-pulse">
                    <Icon name="Save" size={12} color="white" />
                  </div>
                )}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                  <div
                    className="flex items-center justify-center w-5 h-5 rounded-lg flex-shrink-0"
                    style={{ backgroundColor: `${node?.color}20`, color: node?.color }}
                  >
                    <Icon name={node?.icon} size={10} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-foreground truncate">
                      {node?.name || 'Unnamed Node'}
                    </h4>
                  </div>
                  {isContainer && (
                    <button
                      className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10 hover:bg-primary/20 text-primary transition-colors duration-250 pointer-events-auto"
                      onClick={(e) => toggleContainerCollapse(e, node?.id)}
                      aria-label={isCollapsed ? 'Expand container' : 'Collapse container'}
                    >
                      <Icon name={isCollapsed ? 'Plus' : 'Minus'} size={14} />
                    </button>
                  )}
                </div>
                {isMaterialNode ? (
                  // Material node content - show only material name and status
                  (<div className="px-4 py-3">
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-muted-foreground">Material:</span>
                      <span className="text-foreground font-medium truncate ml-2">
                        {node?.data?.materialName || 'Not selected'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Status:</span>
                      <span className="text-foreground font-medium capitalize">
                        {node?.data?.status || 'pending'}
                      </span>
                    </div>
                  </div>)
                ) : isLaborNode ? (
                  // Labor node content - show department, worker, hourly rate, availability
                  (<div className="px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Department:</span>
                      <span className="text-foreground font-medium truncate ml-2 capitalize">
                        {node?.data?.department || 'Not set'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Worker:</span>
                      <span className="text-foreground font-medium truncate ml-2">
                        {node?.data?.workerName || 'Not assigned'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Rate:</span>
                      <span className="text-foreground font-medium">
                        ${node?.data?.hourlyRate || 0}/h
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Status:</span>
                      <span className={`font-medium capitalize ${
                        node?.data?.availabilityStatus === 'available' ? 'text-green-500' :
                        node?.data?.availabilityStatus === 'assigned' ? 'text-blue-500' :
                        node?.data?.availabilityStatus === 'on-break'? 'text-yellow-500' : 'text-muted-foreground'
                      }`}>
                        {node?.data?.availabilityStatus || 'Not set'}
                      </span>
                    </div>
                  </div>)
                ) : isBaseTaskNode ? (
                  // Base Task node content - show calculated labor hours from connected Labor nodes
                  (<div className="px-4 py-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Duration:</span>
                      <span className="text-foreground font-medium">
                        {calculatedLaborHours > 0 ? `${calculatedLaborHours?.toFixed(1)}h` : '0h'}
                      </span>
                    </div>
                    {calculatedLaborHours > 0 && (
                      <div className="flex items-center gap-1 mt-1">
                        <Icon name="Users" size={10} color="var(--color-primary)" />
                        <span className="text-xs text-primary">From labor nodes</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs mt-2">
                      <span className="text-muted-foreground">Workers:</span>
                      <span className="text-foreground font-medium">{node?.data?.workers || 0}</span>
                    </div>
                  </div>)
                ) : (
                  // Standard node content - show duration and workers
                  (<div className="px-4 py-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Duration:</span>
                      <span className="text-foreground font-medium">{node?.data?.duration}h</span>
                    </div>
                    <div className="flex items-center justify-between text-xs mt-2">
                      <span className="text-muted-foreground">Workers:</span>
                      <span className="text-foreground font-medium">{node?.data?.workers}</span>
                    </div>
                  </div>)
                )}
                <button
                  className="absolute -right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center pointer-events-auto hover:scale-110 transition-transform duration-250"
                  onMouseDown={(e) => handleConnectionStart(e, node?.id)}
                  onMouseUp={(e) => handleConnectionEnd(e, node?.id)}
                  aria-label="Create connection"
                >
                  <Icon name="Plus" size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
      {nodes?.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <Icon name="Workflow" size={64} color="var(--color-muted-foreground)" />
            <p className="mt-4 text-lg font-medium text-muted-foreground">
              Drag nodes from the library to start
            </p>
            <p className="mt-2 caption text-muted-foreground">
              Use keyboard shortcuts for faster workflow creation
            </p>
          </div>
        </div>
      )}
      {/* Context Menu */}
      {contextMenu && (
        <div
          className="absolute z-50 bg-card border border-border rounded-lg shadow-xl py-2 min-w-[180px]"
          style={{
            left: `${contextMenu?.x}px`,
            top: `${contextMenu?.y}px`
          }}
          onClick={(e) => e?.stopPropagation()}
        >
          <div className="px-3 py-2 border-b border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</p>
          </div>
          <div className="py-1">
            {[
              { value: 'pending', label: 'Pending', icon: 'Clock', color: 'text-gray-500' },
              { value: 'in-progress', label: 'In Progress', icon: 'PlayCircle', color: 'text-blue-500' },
              { value: 'started', label: 'Started', icon: 'Play', color: 'text-green-500' },
              { value: 'completed', label: 'Completed', icon: 'CheckCircle', color: 'text-green-600' },
              { value: 'blocked', label: 'Blocked', icon: 'XCircle', color: 'text-red-500' }
            ]?.map((status) => (
              <button
                key={status?.value}
                onClick={() => handleStatusChange(contextMenu?.nodeId, status?.value)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2 ${
                  contextMenu?.node?.data?.status === status?.value ? 'bg-muted' : ''
                }`}
              >
                <Icon name={status?.icon} size={16} className={status?.color} />
                <span className="text-foreground">{status?.label}</span>
                {contextMenu?.node?.data?.status === status?.value && (
                  <Icon name="Check" size={14} className="ml-auto text-primary" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowCanvas;